import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import PageSection from 'components/common/PageSection'
import { EditStatusCheckForm } from 'components/status-checks/edit/EditStatusCheckForm'
import { useStatusCheckFormValidationSchemaBuilder } from 'components/status-checks/hooks/use-status-check-form-validation-schema-builder'
import {
  DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME,
  getDefaultStatusCheckFormValuesFromStatusCheck,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { StatusCheckDetailsProvider } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useGetStatusChecksAsServiceStatusChecks } from 'queries/status-checks/use-get-status-checks-as-service-status-checks'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

/**
 * This component is necessary to ensure the loading of all External integrations prior to form initialization.
 * TODO: Handle asynchronous form initialization in EditStatusCheckForm
 */
export const EditStatusCheckFormContainer: React.FC<{
  statusCheck: ServiceStatusCheckFromStatusCheck
  serviceName?: string
}> = ({ statusCheck, serviceName }) => {
  const {
    data: statusChecks,
    error,
    isError
  } = useGetStatusChecksAsServiceStatusChecks({
    retryOnMount: false,
    staleTime: DEFAULT_QUERY_STATUS_CHECKS_STALE_TIME
  })

  const {
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    getAvailableExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()
  const externalIntegrations = getAvailableExternalIntegrations(false)
  const hasFormError = hasErrorExternalIntegrations || isError

  const buildStatusCheckFormValidationSchema =
    useStatusCheckFormValidationSchemaBuilder(
      externalIntegrations,
      /*
       * Exclude the status check being edited to ensure specific validation test criteria
       * are not artificially marked 'invalid' (looking up monitor URL duplication).
       */
      statusChecks?.filter(({ id }) => id !== statusCheck?.id) ?? []
    )

  const form = useForm<StatusCheckFormValues>({
    mode: 'onChange',
    defaultValues: getDefaultStatusCheckFormValuesFromStatusCheck(
      statusCheck,
      externalIntegrations
    ),
    resolver: buildStatusCheckFormValidationSchema
  })

  return (
    <PageSection
      errorMessage={getMessageFromResponseOrError(
        error ?? externalIntegrationsError,
        'Unable to load required data to edit this Health Check. Please try refreshing the page.'
      )}
      hasError={hasFormError}
      isLoading={isFetchingExternalIntegrations}
    >
      <FormProvider {...form}>
        <StatusCheckDetailsProvider
          serviceName={serviceName}
          statusCheckId={statusCheck.id}
        >
          <EditStatusCheckForm statusCheck={statusCheck} />
        </StatusCheckDetailsProvider>
      </FormProvider>
    </PageSection>
  )
}
